import { ViewsService } from "@Engage.Data/services/views/ViewsService";
import { EngageRenderer } from "./actions/EngageRenderer";
import { logService, eventsService, AppEvents } from "@Engage.Core";
import { NewsletterArrivalHandler } from "./newsletter/NewsletterArrivalHandler";
import { UserStore } from "../../plugins/auth/model/UserStore";
import { localStorageService } from "@Engage.Data/services/storage/localStorage/LocalStorageService";
import { StorageKeys } from "@Engage.Data/constants/Constants";
import { registerStorageBridge, setCurrentConversationData } from "../../core/public/StorageBridge";
import { ActivationEngine } from "./actions/activation/ActivationEngine";
import { GiftArticleService } from "./giftArticle/GiftArticleService";
import { DebugPanel } from "./debug/DebugPanel";
import { AccessCodeService } from "./accessCode/AccessCodeService";
export class EngageFlow {
    constructor() {
        this.name = "EngageFlow";
    }
    async init() {
        try {
            logService.log("[EngageFlow] Starting flow", new Date());
            // Register window.Connext.Storage / window.CnnXt.Storage bridge early
            // so g2insights click handlers can access conversation data
            registerStorageBridge();
            DebugPanel.init();
            eventsService.dispatch(AppEvents.Init, null);
            // Warm user cache from localStorage so qualifiers have user data available
            UserStore.warmCache();
            // Dispatch user state events based on current auth state
            this.dispatchUserStateEvents();
            // fire-and-forget — doesn't affect paywall result
            Promise.resolve().then(() => NewsletterArrivalHandler.process());
            eventsService.dispatch(AppEvents.Run, null);
            // --- Access Code Check (mirrors ConneXt v2 AccessByCodes.isSavedCode()) ---
            // If user has a valid saved access code, bypass paywall entirely
            if (AccessCodeService.hasSavedCode()) {
                logService.log("[EngageFlow] Access code found — paywall bypassed");
                eventsService.dispatch(AppEvents.AccessGranted, { Name: 'AccessCode' });
                eventsService.dispatch(AppEvents.Finish, null);
                return;
            }
            // --- Gift Article Check (mirrors ConneXt v2 ValidateGiftArticleLink) ---
            // Must run before paywall — if valid, paywall is bypassed
            const isGiftArticle = await GiftArticleService.validate();
            if (isGiftArticle) {
                logService.log("[EngageFlow] Gift article validated — paywall bypassed");
                eventsService.dispatch(AppEvents.Finish, null);
                return;
            }
            const response = await ViewsService.processView();
            if (response) {
                logService.log("[EngageFlow] Paywall response received", `conversationType=${response.conversationType}`, `conversationId=${response.conversationId}`, `showAction=${response.showAction}`);
                // Cache conversation data for the Storage bridge (g2insights reads it on click)
                setCurrentConversationData({
                    conversationName: response.conversationName,
                    conversationId: response.conversationId,
                    conversationCode: response.conversationCode,
                    campaignName: response.campaignName || response.CampaignName,
                    actions: response.actions
                });
                // Store ActivationForm template for on-demand rendering
                if (response.activationForm || response.ActivationForm) {
                    ActivationEngine.setActivationForm(response.activationForm || response.ActivationForm);
                }
                // Store CodeSetTemplates for access code modal
                const codeSetTemplates = response.codeSetTemplates || response.CodeSetTemplates;
                if (codeSetTemplates) {
                    AccessCodeService.setCodeSetTemplates(codeSetTemplates);
                }
                // Store gift article settings
                const giftArticle = response.giftArticle || response.GiftArticle;
                if (giftArticle) {
                    GiftArticleService.setSettings(giftArticle);
                }
                EngageRenderer.render(response);
                // Bind activation click on page-level buttons (outside templates)
                this.bindPageActivationButtons();
                // Bind access code buttons
                AccessCodeService.bindAccessCodeButtons();
                // Bind gift article buttons
                GiftArticleService.bindGiftArticleButtons();
                // Check if activation was pending before an Auth0 redirect
                ActivationEngine.checkPendingActivation();
                // Refresh debug panel with latest data
                DebugPanel.setPaywallResponse(response);
                DebugPanel.refreshData();
                logService.log("[EngageFlow] Ending flow", new Date());
            }
            else {
                logService.warn("[EngageFlow] No paywall response returned");
            }
            eventsService.dispatch(AppEvents.Finish, null);
            // MakeReRunOnLogin — re-run flow after user logs in
            this.setupReRunOnLogin();
        }
        catch (err) {
            logService.error("[EngageFlow] initialization failed", err);
            eventsService.dispatch(AppEvents.CriticalError, err);
            eventsService.dispatch(AppEvents.LoadError, err);
        }
    }
    dispatchUserStateEvents() {
        const user = UserStore.getUser();
        const state = user === null || user === void 0 ? void 0 : user.UserState;
        if (!state || state === 'LoggedOut') {
            eventsService.dispatch(AppEvents.NotAuthorized, user);
            return;
        }
        // User is logged in — dispatch LoggedIn + specific state event
        eventsService.dispatch(AppEvents.LoggedIn, user);
        eventsService.dispatch(AppEvents.Authorized, user);
        eventsService.dispatch(AppEvents.AnyAccountStatusEvent, user);
        switch (state) {
            case 'Subscribed':
                eventsService.dispatch(AppEvents.HasAccess, user);
                break;
            case 'SubscribedNotEntitled':
                eventsService.dispatch(AppEvents.HasAccessNotEntitled, user);
                break;
            case 'NoActiveSubscription':
                eventsService.dispatch(AppEvents.HasNoActiveSubscription, user);
                break;
        }
    }
    setupReRunOnLogin() {
        const shouldReRun = localStorageService.get(StorageKeys.IsMakeReRunOnLogin, { expandKey: false });
        if (!shouldReRun)
            return;
        logService.log("[EngageFlow] MakeReRunOnLogin is set — listening for login");
        document.addEventListener('onLoginSuccess', async () => {
            logService.log("[EngageFlow] Re-running after login (MakeReRunOnLogin)");
            // clear the flag
            localStorageService.set(StorageKeys.IsMakeReRunOnLogin, '', { expandKey: false });
            // restore scroll in case blocking paywall locked it
            document.body.style.overflow = '';
            // remove existing paywall templates and backdrops
            document.querySelectorAll('.engage-template.Mg2-connext').forEach(el => el.remove());
            document.querySelectorAll('.connext-modal-backdrop, .engage-backdrop').forEach(el => el.remove());
            document.querySelectorAll('.modal-scrollable').forEach(el => el.remove());
            document.body.classList.remove('page-overflow');
            try {
                const response = await ViewsService.processView();
                if (response) {
                    setCurrentConversationData({
                        conversationName: response.conversationName,
                        conversationId: response.conversationId,
                        conversationCode: response.conversationCode,
                        actions: response.actions
                    });
                    EngageRenderer.render(response);
                    this.bindPageActivationButtons();
                }
            }
            catch (err) {
                logService.error("[EngageFlow] Re-run after login failed", err);
            }
        }, { once: true });
    }
    /**
     * Uses event delegation on document to handle [data-mg2-action="activation"] clicks.
     * This works for both static (page-level) and dynamically injected (template) buttons.
     * Mirrors ConneXt v2 Mg2Action.register() which uses dom.on(selector, 'click', handler)
     * with delegation so it catches clicks on elements added after initial page load.
     */
    bindPageActivationButtons() {
        document.addEventListener('click', (e) => {
            var _a;
            const target = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest('[data-mg2-action="activation"]');
            if (target) {
                e.preventDefault();
                ActivationEngine.process();
            }
        });
    }
}
